// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import {
  ActionType,
  IAction,
  IActionChangeDashboardWidget,
  IActionTypeLoginResult,
  IActionTypeSelectDashboardItem,
  IActionTypeSelectGuiItem,
  IActionTypeSelectIos,
} from "actions";
import { isSet } from "utils";
import { EmobilityViewMode, GraphViewMode, IStateCached, PowerViewMode } from "states/cachedstates";
import { IState } from "states/states";
import { EnergySource, InputType, OutputMode } from "parameter";

function makeSiteSelection(state: IStateCached) {
  // site selected?
  console.assert(isSet(state.selectedSite), "No site selected");
  const siteId = state.selectedSite ?? 0;
  if (!isSet(state.siteSelection)) {
    state.siteSelection = [];
  }
  if (!isSet(state.siteSelection[siteId])) {
    state.siteSelection[siteId] = {
      dashboardPanel: [],
      panelSelection: [],
    };
  }
  return state.siteSelection[siteId];
}

function makePanelSelection(state: IStateCached, index: number) {
  const siteSelection = makeSiteSelection(state);
  if (!isSet(siteSelection.panelSelection[index])) {
    siteSelection.panelSelection[index] = {};
  }
  return siteSelection.panelSelection[index];
}

export default function cacheReducer(
  state: IStateCached,
  action: IAction<unknown>,
  rootState: IState,
): IStateCached {
  switch (action.type) {
    case ActionType.ACT_SELECT_SITE:
      // if no payload is given, we keep the cached site
      if (isSet(action.payload)) {
        state.selectedSite = action.payload as number;
      } else {
        console.assert(isSet(state.selectedSite), "No site selected");
      }
      return state;
    case ActionType.ACT_DISCONNECT_SITE:
      state.selectedSite = undefined;
      return state;

    case ActionType.ACT_LOGIN_SUCC: {
      const payload = action.payload as IActionTypeLoginResult;
      // just to be on safe side, remove selected site and chargers for rfid user
      if (payload.isRfidUser) {
        state.selectedSite = undefined;
      }
      return state;
    }

    case ActionType.ACT_SET_FAVORITE: {
      const doSet = action.payload as boolean;
      if (!state.favoriteSites) {
        state.favoriteSites = [];
      }
      if (doSet && isSet(state.selectedSite) && !state.favoriteSites.includes(state.selectedSite)) {
        state.favoriteSites.push(state.selectedSite);
      }
      if (!doSet && isSet(state.selectedSite) && state.favoriteSites.includes(state.selectedSite)) {
        state.favoriteSites = state.favoriteSites.filter(
          (curSite) => curSite !== state.selectedSite,
        );
      }
      return state;
    }

    case ActionType.ACT_SELECT_TARIFFCIRCUITS: {
      const data = action.payload as IActionTypeSelectDashboardItem;
      makePanelSelection(state, data.index).tariffCircuitIds = data.selectedIds!;
      return state;
    }

    case ActionType.ACT_SELECT_IOS: {
      const payload = action.payload as IActionTypeSelectIos;
      const siteSelection = makeSiteSelection(state);
      // determine current selection of inputs
      const selectedInputs =
        siteSelection.inputs ??
        rootState.parameter?.input.map((curItem, curIndex) => curItem.id ?? curIndex) ??
        [];
      // split selection in analog and digital inputs
      const nonAnalogInputs = selectedInputs.filter(
        (curItem) =>
          rootState.parameter?.input.find(
            (curInput, curIndex) => (curInput.id ?? curIndex) === curItem,
          )?.type !== InputType.ANALOG,
      );
      const nonDigitalInputs = selectedInputs.filter((curItem) => {
        const inputType = rootState.parameter?.input.find(
          (curInput, curIndex) => (curInput.id ?? curIndex) === curItem,
        )?.type;
        return inputType === undefined || inputType === InputType.ANALOG;
      });
      // put together new selection with remaining type and new selection
      siteSelection.inputs =
        payload.type === "analog"
          ? [...nonAnalogInputs, ...payload.inputIds]
          : [...nonDigitalInputs, ...payload.inputIds];

      // do the same for outputs
      const selectedOutputs =
        siteSelection.outputs ??
        rootState.parameter?.output.map((curItem, curIndex) => curItem.id ?? curIndex) ??
        [];
      const nonAnalogOutputs = selectedOutputs.filter(
        (curItem) =>
          rootState.parameter?.output.find(
            (curOutput, curIndex) => (curOutput.id ?? curIndex) === curItem,
          )?.mode !== OutputMode.ANALOG,
      );
      const nonDigitalOutputs = selectedOutputs.filter((curItem) => {
        const outputMode = rootState.parameter?.output.find(
          (curOutput, curIndex) => (curOutput.id ?? curIndex) === curItem,
        )?.mode;
        return outputMode === undefined || outputMode === OutputMode.ANALOG;
      });
      siteSelection.outputs =
        payload.type === "analog"
          ? [...nonAnalogOutputs, ...payload.outputIds]
          : [...nonDigitalOutputs, ...payload.outputIds];

      return state;
    }

    case ActionType.ACT_SELECT_MONITOR: {
      const data = action.payload as IActionTypeSelectDashboardItem;
      console.assert(data.selectedIds?.length === 1, "Only one control circuit can be selected");
      makePanelSelection(state, data.index).controlCircuitId = data.selectedIds?.[0];
      return state;
    }

    case ActionType.ACT_SELECT_TRENDMONITOR:
      makeSiteSelection(state).trendMonitor = action.payload as number;
      return state;

    case ActionType.ACT_SELECT_EMOBILITY_WIDGET: {
      const data = action.payload as IActionTypeSelectGuiItem;
      makePanelSelection(state, data.dashboardIndex).eMobilityViewMode =
        data.selection as EmobilityViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_ENERGYSOURCE: {
      const data = action.payload as IActionTypeSelectGuiItem;
      const panelSelection = makePanelSelection(state, data.dashboardIndex);
      // check for change
      if (panelSelection.energySource !== data.selection) {
        panelSelection.energySource = data.selection as EnergySource | undefined;
        // also reset hierachy and circuits as they may not be valid for the new energy source
        panelSelection.hierarchyId = undefined;
        panelSelection.category = undefined;
        panelSelection.tariffCircuitIds = undefined;
      }
      return state;
    }

    case ActionType.ACT_SELECT_HIERARCHY: {
      const data = action.payload as IActionTypeSelectDashboardItem;
      const panelSelection = makePanelSelection(state, data.index);
      // check for change
      if (panelSelection.hierarchyId !== data.hierarchyId) {
        panelSelection.hierarchyId = data.hierarchyId;
        // also reset tariff and control circuits as they may not be valid for the new hierarchy
        if (data.hierarchyUp !== true) {
          // only remove category and selected CircuitInidizes if we change the hierarchy,
          // not if we goe one level up, since all categories are still available there...
          panelSelection.category = undefined;
          panelSelection.tariffCircuitIds = undefined;
        }
      }
      return state;
    }

    case ActionType.ACT_SELECT_CATEGORY: {
      const data = action.payload as IActionTypeSelectGuiItem;
      const panelSelection = makePanelSelection(state, data.dashboardIndex);
      // check for change
      if (panelSelection.category !== data.selection) {
        panelSelection.category = data.selection as string;
        // also reset tariff and control circuits as they may not be valid for the new category
        panelSelection.tariffCircuitIds = undefined;
      }
      return state;
    }

    case ActionType.ACT_SELECT_GRAPHVIEWMODE: {
      const data = action.payload as IActionTypeSelectGuiItem;
      makePanelSelection(state, data.dashboardIndex).graphViewMode =
        data.selection as GraphViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_POWERVIEWMODE: {
      const data = action.payload as IActionTypeSelectGuiItem;
      makePanelSelection(state, data.dashboardIndex).powerViewMode =
        data.selection as PowerViewMode;
      return state;
    }

    case ActionType.ACT_SELECT_DASHBOARD_WIDGET: {
      const payload = action.payload as IActionChangeDashboardWidget;
      const siteSelection = makeSiteSelection(state);
      siteSelection.dashboardPanel[payload.index] = payload.panel;
      return state;
    }

    default:
      return state;
  }
}
